import { action, computed, observable } from 'mobx'
import { mergeMap, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  find,
  map,
  scan,
  takeUntil,
} from 'rxjs/operators'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { AnswersMap, Question } from '@/features/jobs/questions/questions'
import type { QuestionAnswerEditorViewModelFactory } from '@/features/jobs/questions/QuestionAnswerEditorViewModelFactory'
import { QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import { mergeAnswers } from '@/features/jobs/questions/answers-map'
import { deepEqual } from '@/utils/object-util'
import { shareLatest } from '@/utils/rx-util'

/**
 * View-model for question editors.
 */
export class QuestionListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _track$ = new Subject<QuestionEditorViewModel>()
  private readonly _untrack$ = new Subject<QuestionEditorViewModel>()

  /**
   * List of question view-models.
   *
   * @private
   */
  @observable
  private accessor _questionVms: QuestionEditorViewModel[] = []

  /**
   * Answers changes as a stream.
   *
   * Only emits when the answers get changed by the user, not just based
   * on the MobX answers observable. Emits the latest answers on subscribe.
   */
  readonly answersChanged$ = this._track$.pipe(
    // Whenever we start tracking a VM, subscribe to its changes
    mergeMap((vm) =>
      vm.editor.answerChanged$.pipe(
        // ... until we untrack the VM.
        takeUntil(this._untrack$.pipe(find((x) => x === vm))),
      ),
    ),
    map(() => this.answers),
    // Only emit when the answers have changed structurally.
    distinctUntilChanged(deepEqual),
    // Answers to questions that are no longer asked are not part of
    // `answers`. Remember them, so they come back if the user changes their
    // mind and the question becomes relevant again.
    scan<AnswersMap, AnswersMap>(
      (previous, answers) =>
        mergeAnswers(
          previous,
          answers,
          this._questionVms.map((vm) => vm.id),
        ),
      new Map(),
    ),
    shareLatest(),
  )

  constructor(
    private readonly _editorViewModelFactory: QuestionAnswerEditorViewModelFactory,
  ) {
    // Keeps `answersChanged$` hot, so editors tracked while nobody listens
    // still have their changes picked up.
    this.answersChanged$.subscribe()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Question view models.
   */
  @computed get questionVms(): readonly QuestionEditorViewModel[] {
    return this._questionVms
  }

  /**
   * Answers from all questions. Does not include irrelevant answers.
   */
  @computed.struct
  get answers(): AnswersMap {
    const answersMap: AnswersMap = new Map()
    for (const vm of this._questionVms) {
      if (vm.editor.answer) {
        answersMap.set(vm.id, vm.editor.answer)
      }
    }

    return answersMap
  }

  /**
   * Whether a breaker question type is currently visible.
   */
  @computed
  get isBroken() {
    return this._questionVms.some((vm) => vm.editor.type === 'Breaker')
  }

  /**
   * Reconciles the list of questions with the view models.
   *
   * @param questions
   */
  @action.bound
  reconcile(questions: Question[]) {
    const newVms: QuestionEditorViewModel[] = []
    const inputIds = new Set(questions.map((q) => q.id))

    for (const existing of this._questionVms) {
      if (!inputIds.has(existing.id)) {
        this._lifecycle.remove(existing)
        this._untrack$.next(existing)
      }
    }

    for (const question of questions) {
      const existing = this._questionVms.find((vm) => vm.id === question.id)
      if (existing?.editor.type === question.type) {
        existing.set(question)
        newVms.push(existing)
        continue
      }

      if (existing) {
        this._lifecycle.remove(existing)
        this._untrack$.next(existing)
      }

      const editor =
        this._editorViewModelFactory.createAnswerEditorViewModel(question)
      const newInstance = new QuestionEditorViewModel(editor)
      this._lifecycle.manage(newInstance)
      this._track$.next(newInstance)
      newInstance.set(question)
      newVms.push(newInstance)
    }

    this._questionVms = newVms
  }

  /**
   * Validates all the question responses and shows their errors. Returns
   * whether all are valid.
   */
  @action.bound
  validate(): boolean {
    return this._questionVms
      .map((vm) => vm.validate().type === 'Success')
      .every(Boolean)
  }
}
